import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, Briefcase, User2, Clock, MapPin, LogOut, ArrowLeft } from "lucide-react";

function fmtTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function InterviewerDashboard() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/interviewer/upcoming"); setItems(data || []); }
            catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-white sticky top-0 z-30">
                <div className="flex items-center h-16 px-6 gap-6">
                    <div className="flex items-center gap-2">
                        <BrandLogo size={28} />
                        <div className="flex flex-col leading-tight">
                            <span className="font-display font-black tracking-tight text-base">{branding.short_name}</span>
                            <span className="text-[10px] uppercase tracking-widest font-mono text-ikb">Interview mode</span>
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        {user?.role && user.role !== "interviewer" && (
                            <Link
                                to="/dashboard"
                                className="h-9 inline-flex items-center gap-1.5 px-3 border border-border text-xs font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb"
                                data-testid="iv-back-to-app"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to app
                            </Link>
                        )}
                        <div className="hidden md:flex items-center gap-2 text-xs">
                            <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                {(user?.name || "").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="leading-tight">
                                <div className="font-medium">{user?.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-foreground/70">{user?.role === "interviewer" ? "Interviewer" : (user?.role || "User")}</div>
                            </div>
                        </div>
                        <Button variant="outline" className="rounded-none h-9" onClick={async () => { await logout(); nav("/login"); }} data-testid="iv-logout">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>
            <main className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Your interviews</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3"><Calendar className="w-8 h-8 text-ikb" /> Upcoming sessions</h1>
                        <p className="text-foreground/80 mt-2">Only the interviews you've been assigned to. Click any row to open the candidate profile and Q&A.</p>
                    </div>
                    <Link
                        to="/interviewer/day"
                        className="h-10 inline-flex items-center gap-2 px-4 bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-[#001f7a]"
                        data-testid="iv-nav-day"
                    >
                        <Calendar className="w-4 h-4" /> Day planner
                    </Link>
                </div>

                {loading ? (
                    <div className="text-sm font-mono text-foreground/80">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border bg-white p-16 text-center">
                        <h3 className="font-display font-bold text-xl">Nothing scheduled</h3>
                        <p className="text-sm text-foreground/80 mt-2">When a recruiter books you for an interview, it will appear here.</p>
                    </div>
                ) : (
                    <div className="border border-border bg-white divide-y divide-border">
                        {items.map((it) => (
                            <Link key={it.id} to={`/interviewer/interview/${it.id}`} className="block p-5 hover:bg-secondary transition-colors" data-testid={`iv-row-${it.id}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-display font-bold text-lg flex items-center gap-2">
                                            <User2 className="w-4 h-4 text-ikb" /> {it.candidate?.name || "Candidate"}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs font-mono text-foreground/80 mt-1 flex-wrap">
                                            <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {it.job?.title || "Job"}</span>
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtTime(it.scheduled_at)}</span>
                                            {it.location && <><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.location}</span></>}
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-ikb bg-blue-50 text-ikb px-2 py-1">
                                        {it.round_name || `Round ${it.round_number || 1}`}
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
